import { useEffect, useMemo } from "react";

import { type ChartDataset } from "chart.js";
import moment from "moment";

import { CUSTOM_ANNOTATION_ID } from "@features/custom-annotations/annotation-chart-plugin/annotation-plugin-helpers";
import { useAnnotationParam } from "@features/custom-annotations/hooks/use-annotation-param";
import { type DateInterval } from "@north-beam/nb-common";
import { CommonTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";

import { useMmmAnnotations } from "./use-mmm-annotations";

type AnnotationMetadata = {
  annotatedDates: string[];
  datesUsedForTraining?: string[];
  showAnnotations: (dateRange: DateInterval) => void;
  createAnnotation: (annotatedDate: string) => void;
  timezone: string;
  viewable?: any;
};

export type AnnotationDataset = ChartDataset<"line", { date: string }[]> & {
  metadata: AnnotationMetadata;
};

export const useCalendarOfEvents = (
  startDate: string,
  endDate: string,
  queryDateRange: {
    startDate: string;
    endDate: string;
  },
  timezone: string,
) => {
  const {
    showCreateAnnotationOnDate,
    listDateRangeAnnotations,
    listFilteredDateRangeAnnotations,
  } = useAnnotationParam();
  const trackMMMEvent = useTrackMMMEvent(MMMTab.ALL_TABS);

  const {
    annotations: calendarOfEvents,
    fetchAnnotations,
    isAnnotationsLoading,
    isCampaignTypesLoading,
  } = useMmmAnnotations();

  useEffect(() => {
    fetchAnnotations({
      variables: {
        startDate: queryDateRange.startDate,
        endDate: queryDateRange.endDate,
      },
    });
  }, [fetchAnnotations, queryDateRange.startDate, queryDateRange.endDate]);

  const annotationDates = useMemo(() => {
    const dates = [];
    const current = moment(startDate);

    while (current.isSameOrBefore(endDate)) {
      dates.push(moment(current).toISOString().split("T")[0]);
      current.add(1, "day");
    }

    return dates.map((date) => ({ date }));
  }, [endDate, startDate]);

  const metadata = useMemo(() => {
    const annotatedDates = calendarOfEvents.map(
      ({ annotatedDate }) => annotatedDate,
    );
    const datesUsedForTraining = calendarOfEvents
      .filter((e) => e.isUsedForTraining)
      .map(({ annotatedDate }) => annotatedDate);
    const showAnnotations = (dateRange: DateInterval) => {
      listDateRangeAnnotations({ startDate, endDate });
      listFilteredDateRangeAnnotations(dateRange);

      trackMMMEvent({
        component: CommonTrackedComponent.ANNOTATIONS,
        action: "onClick",
        actionInfo: "Show annotations graph icon",
        data: dateRange,
      });
    };
    const createAnnotation = (annotatedDate: string) => {
      listDateRangeAnnotations({ startDate, endDate });
      showCreateAnnotationOnDate(annotatedDate);

      trackMMMEvent({
        component: CommonTrackedComponent.ANNOTATIONS,
        action: "onClick",
        actionInfo: "Add annotation graph icon",
        data: { annotatedDate },
      });
    };

    return {
      annotatedDates,
      datesUsedForTraining,
      showAnnotations,
      createAnnotation,
      timezone,
    };
  }, [
    listDateRangeAnnotations,
    trackMMMEvent,
    calendarOfEvents,
    showCreateAnnotationOnDate,
    listFilteredDateRangeAnnotations,
    startDate,
    endDate,
    timezone,
  ]);

  const annotationsDataset = useMemo(() => {
    return {
      data: annotationDates,
      hidden: true,
      parsing: { xAxisKey: "date" },
      label: CUSTOM_ANNOTATION_ID,
      metadata,
    };
  }, [annotationDates, metadata]);

  return {
    annotationsDataset,
    calendarOfEvents,
    loading: isAnnotationsLoading || isCampaignTypesLoading,
  };
};
